// Crews on line, their totals and the shift's expenses.

import { api, type Schemas } from '@/api/client';

export const SHIFTS_KEY = 'shifts';
export const EXPENSES_KEY = 'expenses';

export type Crew = Schemas['CrewOut'];
export type CrewCreate = Schemas['CrewCreate'];
export type CrewTotals = Schemas['CrewTotalsOut'];
export type Expense = Schemas['ExpenseOut'];
export type ExpenseCreate = Schemas['ExpenseCreate'];
export type ExpenseStatus = Schemas['ExpenseStatus'];

const signalOf = (signal?: AbortSignal) => (signal ? { signal } : {});

export function activeCrews(signal?: AbortSignal) {
  return api.get('/crews/active', { ...signalOf(signal) });
}

export function listCrews(
  query: {
    limit: number;
    offset: number;
    from?: string;
    to?: string;
    /** On line at any moment between these two (the journals' crew filter). */
    worked_from?: string;
    worked_to?: string;
  },
  signal?: AbortSignal,
) {
  return api.get('/crews', { query, ...signalOf(signal) });
}

export function startCrew(body: CrewCreate) {
  return api.post('/crews', { body });
}

export function endCrew(crewId: number) {
  return api.post('/crews/{crew_id}/end', { path: { crew_id: crewId } });
}

export function crewTotals(crewId: number, signal?: AbortSignal) {
  return api.get('/crews/{crew_id}/totals', { path: { crew_id: crewId }, ...signalOf(signal) });
}

export function confirmTotals(crewId: number) {
  return api.post('/crews/{crew_id}/totals/confirm', { path: { crew_id: crewId } });
}

export function listExpenses(
  query: { crew?: number; status?: ExpenseStatus; limit: number; offset: number },
  signal?: AbortSignal,
) {
  return api.get('/expenses', { query, ...signalOf(signal) });
}

export function createExpense(body: ExpenseCreate) {
  return api.post('/expenses', { body });
}

export function reviewExpense(id: number, status: ExpenseStatus, note?: string) {
  return api.post('/expenses/{expense_id}/review', {
    path: { expense_id: id },
    body: { status, ...(note ? { note } : {}) },
  });
}

// --- the operator's desk shift (TZ 7.6, STAGE_4 4.6) ------------------------------------

export const OPERATOR_SHIFT_KEY = 'operator-shift';
export type OperatorShift = Schemas['OperatorShiftOut'];

/** The user's latest shift; open when `ended_at` is null. */
export async function latestOperatorShift(
  userId: number,
  signal?: AbortSignal,
): Promise<OperatorShift | null> {
  const page = await api.get('/operator-shifts', {
    query: { user_id: userId, limit: 1, offset: 0 },
    ...signalOf(signal),
  });
  return page.items[0] ?? null;
}

export function startOperatorShift() {
  return api.post('/operator-shifts/start');
}

export function endOperatorShift(shiftId: number, handoverNote: string) {
  return api.post('/operator-shifts/{shift_id}/end', {
    path: { shift_id: shiftId },
    body: handoverNote ? { handover_note: handoverNote } : {},
  });
}
